/**
 * 群禁言/媒体限制 → 输入栏限制态（14.15a；两线程共用纯逻辑单点）。
 *
 * 口径（msgsend.go sendGroupMsg + models/group.go IsMessageAllowed 实锤）：
 * - muted（整栏禁言）= MyInfo.Muted==1（个人禁言）或「非特权成员且 Group.
 *   Stillness==1」（全体禁言）。特权=群主（OwnerId==自己或 Type==2）/管理员
 *   （Type==1）——与后端 isAdmin（Type 1/2）一致，OwnerId 判定是防御冗余。
 * - blockedMedia（禁媒体类型）= allow_media 掩码（"2,4,7,8" 子集）里缺的位；
 *   空串/"-1" =全允许；特权成员恒不受限。贴纸 44 归并图片位 2（后端
 *   IsMessageAllowed 同口径，14.16 贴纸面板用 isMediaBlocked 判）。
 * - ⚠️ 刻意偏差：旧版把 allow_media=="-1" 当「全员禁言」（chat_screen.dart
 *   _isMutedByGroupProfile）——那是对 msgsend 死代码分支的误读：IsMessageAllowed
 *   对 "-1" 恒返 true，外层条件永不成立，"-1" 实为全允许（模型注释同）。
 *   新版按后端真实语义。
 * - 文本/回复等非媒体类型不受 allow_media 限制（后端只查 2/4/7/8/44）。
 * - 例外名单（CheckGroupExcept 可绕过媒体限制）客户端无从查询，不参与判定
 *   ——例外成员发送由服务端放行，UI 多拦一步的代价由 14.26 例外名单管理页
 *   届时评估（旧版同样不判）。
 */

/** /group/profile 收窄后的限制相关字段（group-api.ts 产出，session 缓存单元） */
export interface GroupComposerSource {
  /** Group.Stillness：1=全体禁言 */
  readonly stillness: number;
  /** Group.AllowMedia 原始串："2,4,7,8" 子集 / ""、"-1"=全允许 */
  readonly allowMedia: string;
  /** MyInfo.Type：0 成员 / 1 管理员 / 2 群主 */
  readonly myType: number;
  /** MyInfo.Muted：1=我被个人禁言 */
  readonly myMuted: number;
  /** Group.OwnerId === MyInfo.UserId（防御冗余，正常群主 Type 已是 2） */
  readonly isOwner: boolean;
  /** Group.Notice：群公告原文（21ah 聊天页公告条；空串=未设置） */
  readonly notice: string;
}

/** 我的群内角色（14.25a 单点；群删除菜单/批量删除/设置页按角色渲染都用它） */
export type GroupRole = 0 | 1 | 2;

/** 推给主线程的输入栏限制态（14.15b UI 按此渲染；服务端二次校验兜底） */
export interface ComposerRestriction {
  /** 整栏禁言：隐藏发送、禁输入（旧版 _groupComposerMuted 同语义） */
  readonly muted: boolean;
  /** 被禁的媒体 msgType（⊆{2,4,7,8}）；判定用 isMediaBlocked（含 44→2 归并） */
  readonly blockedMedia: readonly number[];
  /**
   * 我的角色（14.25a；group/profile MyInfo.Type + OwnerId 兜底，groupRoleOf）。
   * 私聊/拉取失败恒 0——群删除等管理动作 fail-closed（messageMenuModel 口径：
   * 普通成员连自己的群消息都不能删，func_chat.go 实锤），与限制态的 fail-open
   * 方向相反但同样安全：少显示一个入口，服务端兜底。
   */
  readonly myRole: GroupRole;
  /**
   * 群公告原文（21ah）。空串=未设置，聊天页不出公告条。
   *
   * 顺着**既有**的限制态通道透出，而不是让聊天页另取一份：公告与 muted/
   * blockedMedia/myRole 同源于 group/profile 快照（14.25a 单点），
   * 另开一条路就意味着第二份缓存与第二次失效找平。私聊恒空串。
   */
  readonly notice: string;
}

/** 私聊/拉取失败/未知群的缺省：不限制（fail-open，服务端兜底拒发） */
export const UNRESTRICTED: ComposerRestriction = {
  muted: false,
  blockedMedia: [],
  myRole: 0,
  notice: '',
};

/** allow_media 掩码的全集（image/voice/video/file；与后端 IsMessageAllowed 一致） */
export const MEDIA_MASK_TYPES: readonly number[] = [2, 4, 7, 8];

/** 贴纸（44）与图片共用开关（IsMessageAllowed 归并口径） */
const STICKER = 44;

export function isPrivileged(src: GroupComposerSource): boolean {
  return src.isOwner || src.myType === 1 || src.myType === 2;
}

/**
 * 角色推导单点（旧版 _loadGroupDetail 同口径：Type==2 或 OwnerId==自己 →
 * owner；Type==1 → admin；其余 member）。null（无来源/私聊）→ 0。
 */
export function groupRoleOf(src: GroupComposerSource | null): GroupRole {
  if (!src) return 0;
  if (src.isOwner || src.myType === 2) return 2;
  return src.myType === 1 ? 1 : 0;
}

/** allow_media 原始串 → 被禁类型（空/"-1"/全集=空数组）。非掩码值忽略 */
export function blockedMediaOf(allowMedia: string): number[] {
  const raw = allowMedia.trim();
  if (raw === '' || raw === '-1') return [];
  const allowed = new Set(
    raw
      .split(',')
      .map((s) => Number.parseInt(s.trim(), 10))
      .filter((n) => Number.isFinite(n)),
  );
  return MEDIA_MASK_TYPES.filter((t) => !allowed.has(t));
}

/** 限制态推导单点。null（无来源）→ 不限制 */
export function composerRestrictionOf(src: GroupComposerSource | null): ComposerRestriction {
  if (!src) return UNRESTRICTED;
  const myRole = groupRoleOf(src);
  // 公告对谁都一样（管理员也要看见），所以两条分支都带上
  const notice = src.notice;
  if (isPrivileged(src)) {
    return { muted: src.myMuted === 1, blockedMedia: [], myRole, notice };
  }
  const muted = src.myMuted === 1 || src.stillness === 1;
  const blockedMedia = blockedMediaOf(src.allowMedia);
  return { muted, blockedMedia, myRole, notice };
}

/** 某 msgType 是否被媒体掩码禁发（贴纸 44 归并图片位；非媒体类型恒 false） */
export function isMediaBlocked(
  restriction: Pick<ComposerRestriction, 'blockedMedia'>,
  msgType: number,
): boolean {
  const t = msgType === STICKER ? 2 : msgType;
  return restriction.blockedMedia.includes(t);
}
